import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/contexts/AuthContext";
import { PageHeader, PageBody } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Link as LinkIcon, Plus, Copy, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const PLATFORMS = ["tiktok", "facebook", "instagram", "youtube", "twitter", "other"];

const EMPTY = {
  destination_url: "",
  label: "",
  product_id: "",
  platform: "tiktok",
  utm_source: "",
  utm_medium: "",
  utm_campaign: "",
  utm_content: "",
  notes: "",
};

export default function LinksPage() {
  const [links, setLinks] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, p] = await Promise.all([
        axios.get(`${API}/links`),
        axios.get(`${API}/products`),
      ]);
      setLinks(l.data);
      setProducts(p.data);
    } catch (e) {
      toast.error("Failed to load");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pickProduct = (id) => {
    if (id === "_none") {
      setForm({ ...form, product_id: "", destination_url: "" });
      return;
    }
    const p = products.find((x) => x.id === id);
    if (p) setForm({ ...form, product_id: id, destination_url: p.affiliate_url, label: form.label || p.title });
  };

  const create = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.product_id) delete body.product_id;
      Object.keys(body).forEach((k) => body[k] === "" && delete body[k]);
      await axios.post(`${API}/links`, body);
      toast.success("Link created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (code) => {
    if (!window.confirm("Delete this link?")) return;
    try {
      await axios.delete(`${API}/links/${code}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const copy = async (s, c) => {
    try {
      await navigator.clipboard.writeText(s);
      toast.success(`Copied /r/${c}`);
    } catch {
      toast.error("Clipboard blocked");
    }
  };

  return (
    <>
      <PageHeader
        icon={LinkIcon}
        title="Affiliate Links"
        subtitle="Short links with UTM presets · /api/r/{code} redirects + clicks"
        testid="links-header"
        actions={
          <Button onClick={() => setOpen(true)} className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold" data-testid="new-link-btn">
            <Plus className="h-4 w-4 mr-2" /> New Link
          </Button>
        }
      />
      <PageBody>
        <div className="ap-panel overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 text-[11px] mono uppercase text-zinc-500 border-b border-zinc-800">
            <div className="col-span-4">Short / Destination</div>
            <div className="col-span-3">Product</div>
            <div className="col-span-2">Platform · UTM</div>
            <div className="col-span-1 text-right">Clicks</div>
            <div className="col-span-2"></div>
          </div>
          {links.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-500" data-testid="links-empty">
              No links yet. Click <span className="mono">New Link</span>.
            </div>
          ) : (
            <ul data-testid="links-list">
              {links.map((l) => (
                <li key={l.short_code} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-900 last:border-0 hover:bg-zinc-900/30" data-testid={`link-row-${l.short_code}`}>
                  <div className="md:col-span-4 min-w-0">
                    <div className="text-xs text-zinc-500 mb-0.5">{l.label}</div>
                    <a href={l.short_url} target="_blank" rel="noreferrer" className="block text-sm text-emerald-300 truncate hover:underline mono" data-testid={`link-short-${l.short_code}`}>
                      {l.short_url}
                    </a>
                    <div className="text-[11px] text-zinc-500 truncate" title={l.destination_url}>{l.destination_url}</div>
                  </div>
                  <div className="md:col-span-3 text-xs text-zinc-300 min-w-0 truncate">
                    {l.product ? l.product.title : <span className="text-zinc-600">—</span>}
                  </div>
                  <div className="md:col-span-2 text-[11px] flex flex-wrap gap-1">
                    <Badge variant="outline" className="border-zinc-700 mono text-[10px] text-zinc-400">{l.platform}</Badge>
                    {l.utm_campaign && <Badge variant="outline" className="border-zinc-700 mono text-[10px] text-zinc-500">{l.utm_campaign}</Badge>}
                  </div>
                  <div className="md:col-span-1 md:text-right mono text-sm text-amber-300" data-testid={`link-clicks-${l.short_code}`}>{l.click_count}</div>
                  <div className="md:col-span-2 flex md:justify-end gap-1">
                    <button onClick={() => copy(l.short_url, l.short_code)} className="p-1.5 rounded-md hover:bg-zinc-900 text-zinc-400 hover:text-emerald-400" data-testid={`link-copy-${l.short_code}`}>
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => remove(l.short_code)} className="p-1.5 rounded-md hover:bg-zinc-900 text-zinc-400 hover:text-rose-400" data-testid={`link-delete-${l.short_code}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PageBody>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="new-link-modal">
          <DialogHeader><DialogTitle>New affiliate link</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1 ap-scroll">
            <div>
              <Label className="text-zinc-400 text-xs">Product (optional)</Label>
              <Select value={form.product_id || "_none"} onValueChange={pickProduct}>
                <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-700" data-testid="link-product-select"><SelectValue placeholder="No product" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-[260px]">
                  <SelectItem value="_none">No product</SelectItem>
                  {products.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`link-product-option-${p.id}`}>{p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Destination URL</Label>
              <Input value={form.destination_url} onChange={(e) => setForm({ ...form, destination_url: e.target.value })} placeholder="https://..." className="mt-1 bg-zinc-900 border-zinc-700" data-testid="link-destination-input" required />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-zinc-400 text-xs">Label</Label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-700" data-testid="link-label-input" required />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Platform</Label>
                <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                  <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-700" data-testid="link-platform-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                    {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-zinc-400 text-xs">utm_source</Label>
                <Input value={form.utm_source} onChange={(e) => setForm({ ...form, utm_source: e.target.value })} placeholder={form.platform} className="mt-1 bg-zinc-900 border-zinc-700" data-testid="link-utm-source-input" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">utm_medium</Label>
                <Input value={form.utm_medium} onChange={(e) => setForm({ ...form, utm_medium: e.target.value })} placeholder="affiliate" className="mt-1 bg-zinc-900 border-zinc-700" data-testid="link-utm-medium-input" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">utm_campaign</Label>
                <Input value={form.utm_campaign} onChange={(e) => setForm({ ...form, utm_campaign: e.target.value })} placeholder="auto from product" className="mt-1 bg-zinc-900 border-zinc-700" data-testid="link-utm-campaign-input" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">utm_content</Label>
                <Input value={form.utm_content} onChange={(e) => setForm({ ...form, utm_content: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-700" data-testid="link-utm-content-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-zinc-700 bg-zinc-900 text-zinc-200" data-testid="cancel-new-link-btn">Cancel</Button>
            <Button onClick={create} disabled={saving || !form.destination_url || !form.label} className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold" data-testid="save-link-btn">
              {saving ? "Saving…" : "Create link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
